import { useState, useEffect } from "react";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Card,
    CardBody,
} from "@heroui/react";
import { LuCheck, LuTriangleAlert } from "react-icons/lu";
import { useFetchData, usePostData } from "@/hooks";
import { ThemedText } from "@/components/ui";
import { showToast } from "@/utils";

/**
 * ChangeStatusModal - Reusable modal for changing item status
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {Object} item - Item to change status (must have id, name, status)
 * @param {string} itemName - Item name to display in modal
 * @param {string} endpoint - API endpoint for status change (e.g., "materials/{id}/change-status")
 * @param {string} statusEndpoint - API endpoint to fetch status options (e.g., "select/active-status")
 * @param {string} statusField - Field name to send in request (default: "active_status_id")
 * @param {string} title - Modal title (default: "Ubah Status")
 * @param {Function} onSuccess - Callback after successful status change
 * @param {Array} statusIdsRequiringConfirmation - Array of status IDs that require additional confirmation
 * @param {string} confirmationMessage - Message to display when confirmation is required
 * @param {Function} renderCustomContent - Render prop for custom content (customData, setCustomData) => ReactNode
 * @param {Array} statusIdsRequiringCustomContent - Array of status IDs that require custom content
 * @param {Function} validateCustomContent - Validation function (customData) => string|null (returns error message or null)
 * @param {string} size - Modal size (default: "sm")
 */
const ChangeStatusModal = ({
    isOpen,
    onClose,
    item,
    itemName,
    endpoint,
    statusEndpoint,
    statusField,
    title = "Ubah Status",
    onSuccess,
    statusIdsRequiringConfirmation = [],
    confirmationMessage = "Tindakan ini tidak dapat dibatalkan. Apakah Anda yakin?",
    renderCustomContent = null,
    statusIdsRequiringCustomContent = [],
    validateCustomContent = null,
    size = "sm",
}) => {
    const [selectedStatusId, setSelectedStatusId] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showCustomContent, setShowCustomContent] = useState(false);
    const [customData, setCustomData] = useState({});

    // Fetch available statuses from provided endpoint
    const { data: statuses, isLoading: isLoadingStatuses } =
        useFetchData(statusEndpoint);

    // Mutation for changing status
    const mutation = usePostData(endpoint?.replace("{id}", item?.id), false);

    // Reset states when modal opens
    useEffect(() => {
        if (isOpen && item) {
            setSelectedStatusId(null);
            setShowConfirmation(false);
            setShowCustomContent(false);
            setCustomData({});
        }
    }, [isOpen, item]);

    // Check if selected status requires custom content
    const requiresCustomContent =
        renderCustomContent &&
        statusIdsRequiringCustomContent.includes(selectedStatusId);

    const handleSubmit = () => {
        if (!selectedStatusId) return;

        // Step 1: Check if custom content is required but not shown yet
        if (requiresCustomContent && !showCustomContent) {
            setShowCustomContent(true);
            return;
        }

        // Step 1.5: Validate custom content if required
        if (showCustomContent && validateCustomContent) {
            const validationError = validateCustomContent(customData);
            if (validationError) {
                showToast("error", "Validasi Gagal", validationError);
                return;
            }
        }

        // Step 2: Check if confirmation is required but not shown yet
        const requiresConfirmation =
            statusIdsRequiringConfirmation.length > 0 &&
            statusIdsRequiringConfirmation.includes(selectedStatusId);

        if (requiresConfirmation && !showConfirmation) {
            setShowConfirmation(true);
            return;
        }

        // Step 3: Submit with status and custom data
        mutation.mutate(
            { [statusField]: selectedStatusId, ...customData },
            {
                method: "patch",
                onSuccess: (response) => {
                    showToast(
                        "success",
                        "Berhasil",
                        response.message || `Status berhasil diubah`,
                    );
                    onSuccess?.(response);
                    onClose();
                },
                onError: (err) => {
                    showToast(
                        "error",
                        "Gagal",
                        err.response?.data?.message ||
                            "Terjadi kesalahan pada sistem. Silakan coba beberapa saat lagi atau hubungi administrator.",
                    );
                },
            },
        );
    };

    const handleBack = () => {
        if (showConfirmation) {
            setShowConfirmation(false);
        } else if (showCustomContent) {
            setShowCustomContent(false);
        }
    };

    // Derive relationship key from field name (e.g., "active_status_id" → "active_status")
    const relationshipKey = statusField?.replace(/_id$/, "");
    const currentStatusId = item?.[relationshipKey]?.id;

    // Get selected status name for confirmation
    const selectedStatus = statuses?.find((s) => s.value === selectedStatusId);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={size}
            classNames={{
                header: "border-b border-default-100",
                footer: "border-t border-default-100",
            }}
            isDismissable={!mutation.isLoading}
            isKeyboardDismissDisabled={mutation.isLoading}
            hideCloseButton={mutation.isLoading}
        >
            <ModalContent>
                <ModalHeader className="flex flex-col gap-1">
                    <ThemedText className="font-semibold">{title}</ThemedText>
                    <ThemedText
                        variant="secondary"
                        size="xs"
                        className="font-normal"
                    >
                        Ubah status untuk{" "}
                        <ThemedText
                            as="span"
                            variant="default"
                            size="xs"
                            className="font-medium"
                        >
                            {itemName || item?.name}
                        </ThemedText>
                    </ThemedText>
                </ModalHeader>

                <ModalBody className="py-4">
                    {isLoadingStatuses ? (
                        <div className="flex flex-col items-center justify-center gap-3 py-6">
                            <Spinner size="lg" color="primary" />
                            <ThemedText variant="secondary" size="xs">
                                Memuat status...
                            </ThemedText>
                        </div>
                    ) : showConfirmation ? (
                        // Confirmation Step
                        <div className="flex flex-col gap-4">
                            <Card
                                className="bg-warning-50 border-warning-200 border"
                                shadow="none"
                            >
                                <CardBody className="flex flex-row items-start gap-3 p-4">
                                    <div className="bg-warning-100 text-warning-600 shrink-0 rounded-full p-2">
                                        <LuTriangleAlert size={20} />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <ThemedText
                                            size="sm"
                                            className="font-semibold"
                                        >
                                            Konfirmasi Perubahan Status
                                        </ThemedText>
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                        >
                                            Anda akan mengubah status ke{" "}
                                            <ThemedText
                                                as="span"
                                                size="xs"
                                                className="font-semibold"
                                            >
                                                {selectedStatus?.title}
                                            </ThemedText>
                                        </ThemedText>
                                        <ThemedText
                                            size="xs"
                                            variant="warning"
                                            className="mt-2"
                                        >
                                            {confirmationMessage}
                                        </ThemedText>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                    ) : showCustomContent && renderCustomContent ? (
                        // Custom Content Step
                        <div className="flex flex-col gap-4">
                            <Card shadow="none">
                                <CardBody className="p-0">
                                    <div className="mb-4 flex flex-col gap-1">
                                        <ThemedText
                                            size="sm"
                                            className="font-semibold"
                                        >
                                            Informasi Tambahan
                                        </ThemedText>
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                        >
                                            Status akan diubah ke{" "}
                                            <ThemedText
                                                as="span"
                                                size="xs"
                                                className="font-semibold"
                                            >
                                                {selectedStatus?.title}
                                            </ThemedText>
                                        </ThemedText>
                                    </div>
                                    {renderCustomContent(
                                        customData,
                                        setCustomData,
                                        selectedStatus,
                                    )}
                                </CardBody>
                            </Card>
                        </div>
                    ) : (
                        // Status Selection
                        <div className="flex flex-col gap-2">
                            {statuses?.map((status) => {
                                const isCurrentStatus =
                                    status.value === currentStatusId;
                                const isSelected =
                                    status.value === selectedStatusId;

                                return (
                                    <button
                                        key={status.value}
                                        type="button"
                                        onClick={() =>
                                            !isCurrentStatus &&
                                            setSelectedStatusId(status.value)
                                        }
                                        disabled={isCurrentStatus}
                                        className={`group relative flex w-full items-center justify-between rounded-xl px-4 py-3.5 transition-all duration-200 outline-none ${
                                            isCurrentStatus
                                                ? "bg-default-50 cursor-not-allowed opacity-100"
                                                : isSelected
                                                  ? "bg-primary-50 ring-primary-500 cursor-pointer shadow-sm ring-1"
                                                  : "hover:bg-default-50 cursor-pointer active:scale-[0.98]"
                                        } `}
                                    >
                                        <div className="flex flex-col items-start gap-0.5">
                                            <ThemedText
                                                size="sm"
                                                variant={
                                                    isCurrentStatus
                                                        ? "secondary"
                                                        : isSelected
                                                          ? "primary"
                                                          : "default"
                                                }
                                                className="font-medium transition-colors"
                                            >
                                                {status.title}
                                            </ThemedText>
                                            {isCurrentStatus && (
                                                <ThemedText
                                                    variant="caption"
                                                    size="xs"
                                                >
                                                    Current
                                                </ThemedText>
                                            )}
                                        </div>

                                        <div
                                            className={`flex h-4 w-4 items-center justify-center rounded-full border transition-all duration-200 ${
                                                isCurrentStatus
                                                    ? "border-default-300 bg-default-200 text-default-400"
                                                    : isSelected
                                                      ? "bg-primary-500 border-primary-500 scale-110 text-white"
                                                      : "border-default-300 group-hover:border-default-400"
                                            } `}
                                        >
                                            {isCurrentStatus ? (
                                                <div className="bg-default-400 h-2 w-2 rounded-full" />
                                            ) : isSelected ? (
                                                <LuCheck size={10} />
                                            ) : (
                                                <div className="h-full w-full rounded-full opacity-0" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    {showConfirmation ? (
                        <>
                            <Button
                                variant="light"
                                onPress={handleBack}
                                size="sm"
                                className="text-default-500 hover:text-default-900 font-medium"
                                isDisabled={mutation.isLoading}
                            >
                                Kembali
                            </Button>
                            <Button
                                color="warning"
                                onPress={handleSubmit}
                                isLoading={mutation.isLoading}
                                size="sm"
                                radius="full"
                            >
                                Ya, Lanjutkan
                            </Button>
                        </>
                    ) : showCustomContent ? (
                        <>
                            <Button
                                variant="light"
                                onPress={handleBack}
                                size="sm"
                                className="text-default-500 hover:text-default-900 font-medium"
                                isDisabled={mutation.isLoading}
                            >
                                Kembali
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleSubmit}
                                isLoading={mutation.isLoading}
                                size="sm"
                                radius="full"
                            >
                                Simpan Perubahan
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="light"
                                onPress={onClose}
                                size="sm"
                                className="text-default-500 hover:text-default-900 font-medium"
                                isDisabled={mutation.isLoading}
                            >
                                Batal
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleSubmit}
                                isLoading={mutation.isLoading}
                                isDisabled={!selectedStatusId}
                                size="sm"
                                radius="full"
                            >
                                {requiresCustomContent
                                    ? "Lanjutkan"
                                    : "Simpan Perubahan"}
                            </Button>
                        </>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ChangeStatusModal;
